import { useMutation } from '@apollo/react-hooks'
import { gql } from 'apollo-boost'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputField,
  Typography,
  makeStyles,
  useTranslation
} from '@paintscout/ui'
import type { MuiTheme as Theme, DialogProps } from '@paintscout/ui'
import { DropdownSelect, useClientOptions, useDialogs } from '@paintscout/ui-business'
import { formatCurrency, getObjectLabels, getPaymentMethodOptions, getRefundableAmount } from '@paintscout/util/builder'
import type { Payment, QuoteDocument } from 'paintscout'
import CloseIcon from '@material-ui/icons/Close'
import React, { useState } from 'react'
import { useSnackbar } from 'notistack'
import { track } from 'shared/util/analytics'
import { useQuote } from '../../context'

const useStyles = makeStyles<Theme>(
  (theme) => ({
    dialogPaper: {
      overflow: 'visible',
      '@media (max-height: 700px)': {
        overflow: 'auto'
      },
      [theme.breakpoints.down('xs')]: {
        overflow: 'auto'
      }
    },
    dialogContent: {
      overflow: 'visible',
      '@media (max-height: 700px)': {
        overflow: 'auto'
      },
      [theme.breakpoints.down('xs')]: {
        overflow: 'auto'
      }
    }
  }),
  { name: 'RefundPaymentDialog' }
)

const REFUND_PAYMENT = gql`
  mutation refundPayment($activityId: String!, $amount: Float!, $note: String, $method: String) {
    refundPayment(activityId: $activityId, amount: $amount, note: $note, method: $method) {
      refundTxnId
      updatedQuote
    }
  }
`

export interface RefundPaymentDialogProps extends DialogProps {
  payment: Payment
  allPayments: Payment[]
  onClose?: (updatedQuote: QuoteDocument) => void
}

export default function RefundPaymentDialog({
  open,
  payment,
  allPayments,
  onClose,
  ...baseDialogProps
}: RefundPaymentDialogProps) {
  const classes = useStyles()
  const { options } = useClientOptions()
  const { dismissDialog, dismissAllDialogs } = useDialogs()
  const { quote, updateQuote } = useQuote()
  const { enqueueSnackbar } = useSnackbar()
  const { __ } = useTranslation()

  const maxAmount = getRefundableAmount(payment, allPayments)
  const hasPriorRefund = payment.amount > maxAmount
  const [amount, setAmount] = useState<number>(maxAmount)
  const [note, setNote] = useState('')
  const [method, setMethod] = useState<string | undefined>(payment.type)

  const { currency } = getObjectLabels({ options })
  const typeOptions = getPaymentMethodOptions(options).map((opt) => ({ ...opt, value: opt.label }))
  const typeValue = method ? { key: method, label: method, value: method } : null

  const [refundPayment, { loading }] = useMutation(REFUND_PAYMENT)

  const isValid = amount > 0 && amount <= maxAmount

  async function handleConfirm() {
    try {
      const result = await refundPayment({
        variables: {
          activityId: payment.activityId,
          amount,
          note: note || undefined,
          method: payment.manual ? method : undefined
        }
      })
      const updatedQuote: QuoteDocument = result.data?.refundPayment?.updatedQuote
      if (updatedQuote) {
        await updateQuote({ quote: updatedQuote, autosave: false, blockDirty: true })
      }
      track('payment_refunded', {
        quoteId: updatedQuote?._id ?? quote?._id,
        provider: payment.manual ? 'manual' : payment.provider,
        method: payment.type,
        amount,
        isPartial: amount < payment.amount
      })
      enqueueSnackbar(__('Refund issued successfully.'), { variant: 'success' })
      dismissAllDialogs()
      onClose?.(updatedQuote)
    } catch (err) {
      const graphqlMsg = (err as any)?.graphQLErrors?.[0]?.message
      const isStoreError =
        !graphqlMsg &&
        typeof (err as any)?.message === 'string' &&
        (err as any).message.startsWith('Error writing result to store')
      const message =
        graphqlMsg ??
        (isStoreError
          ? __('Unable to process refund. Please try again.')
          : err instanceof Error
            ? err.message
            : __('Refund failed'))
      enqueueSnackbar(message, { variant: 'error' })
    }
  }

  return (
    <Dialog open={open} maxWidth="xs" fullWidth classes={{ paper: classes.dialogPaper }} {...baseDialogProps}>
      <DialogTitle
        rightContent={
          <IconButton size="small" onClick={dismissDialog} disabled={loading}>
            <CloseIcon />
          </IconButton>
        }
      >
        {__('Refund Payment')}
      </DialogTitle>
      <DialogContent classes={{ root: classes.dialogContent }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="body2" color="textSecondary">
              {hasPriorRefund
                ? __(
                    'Available to refund: %s (original: %s)',
                    formatCurrency({ options, value: maxAmount }),
                    formatCurrency({ options, value: payment.amount })
                  )
                : __('Original payment: %s', formatCurrency({ options, value: maxAmount }))}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <InputField
              label={__('Refund Amount')}
              value={amount}
              placeholder={'0'}
              onChange={(e) => setAmount(Number.parseFloat(e.target.value) ?? ('' as any))}
              format="price"
              prefix={currency.symbol}
              fullWidth
              autoFocus
              error={amount > maxAmount}
              showErrorBeforeValidation={amount > maxAmount}
            />
            {amount > maxAmount && (
              <Typography variant="caption" color="error">
                {hasPriorRefund
                  ? __('Cannot exceed available refund amount')
                  : __('Cannot exceed original payment amount')}
              </Typography>
            )}
          </Grid>
          {payment.manual ? (
            <Grid item xs={12}>
              <DropdownSelect
                variant="single"
                label={__('Method')}
                value={typeValue}
                options={typeOptions}
                onChange={(val: any) => setMethod(val?.value)}
                fullWidth
                searchable
                placeholder={{ selectPlaceholder: __('Select a Payment Method'), plural: __('Payment Methods') }}
              />
            </Grid>
          ) : (
            payment.type && (
              <Grid item xs={12}>
                <InputField label={__('Method')} value={payment.type} readOnly disabled fullWidth />
              </Grid>
            )
          )}
          <Grid item xs={12}>
            <InputField
              label={__('Client Note')}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              fullWidth
              multiline
              rows={2}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={handleConfirm} disabled={!isValid} loading={loading}>
          {__('Issue Refund')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
